import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	type FieldValues,
	FormProvider,
	type Resolver,
	useForm,
	useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { LoadingIndicator } from '@/components/LoadingIndicator/LoadingIndicator';
import { Modal } from '@/components/Modal/Modal';
import { useDeleteDcrAttachmentMutation } from '@/core/dcr/graphql/mutation/DeleteDcrAttachment.hook';
import { usePostDcrMutation } from '@/core/dcr/graphql/mutation/PostDCR.hook';
import { usePicklistValues } from '@/core/dcr/hooks/usePicklistValues';
import { useUnavailableFields } from '@/core/dcr/hooks/useUnavailableFields';
import { toDcrInput } from '@/core/dcr/serialize/toDcrInput';
import {
	type Country,
	type DcrhcoInput,
	type DcrhcpInput,
	EntityType
} from '@/core/graphql/generated/types';
import { paths } from '@/core/router/paths';

import { useGetAllCountriesQuery } from '../_graphql/query/GetAllCountries.hook';
import { useGetPreferredCountryQuery } from '../_graphql/query/GetPreferredCountry.hook';
import {
	buildDcrFormDefaults,
	buildDcrFormSchema
} from '../_schemas/dcrForm.schema';
import type { DcrAttachment } from '../Attachments/attachment.types';
import { useAttachmentUpload } from '../Attachments/useAttachmentUpload';
import { DcrFormFieldsProvider } from '../DcrFormFieldsProvider';
import { DcrSuccessModalContent } from '../DcrSuccessModalContent/DcrSuccessModalContent';
import { DiscardChangesWarningModal } from '../DiscardChangesWarningModal/DiscardChangesWarningModal';
import { EntityForm } from '../EntityForm/EntityForm';
import { PickCountryStep } from '../PickCountryStep/PickCountryStep';
import { PickCountryStepActions } from '../PickCountryStep/PickCountryStepActions';
import { ReviewStep } from '../Review/ReviewStep';
import { ReviewStepActions } from '../Review/ReviewStepActions';

import { dcrFormModalStyles as sx } from './DcrFormModal.styles';
import { FormStepActions } from './FormStepActions';

type Props = {
	open: boolean;
	entityType: EntityType;
	onClose: () => void;
	onExited?: () => void;
	onLoaded: () => void;
};

type Step = 'PICK_COUNTRY' | 'FORM' | 'REVIEW' | 'SUCCESS';

export const DcrFormModal = ({
	open,
	entityType,
	onClose,
	onExited,
	onLoaded
}: Props) => {
	const { t } = useTranslation();
	const { pathname } = useLocation();
	const scrollRef = useRef<HTMLDivElement>(null);

	const [formStep, setFormStep] =
		useState<Exclude<Step, 'PICK_COUNTRY'>>('FORM');
	const [pickedCountryId, setPickedCountryId] = useState<string>();
	const [pendingCountryId, setPendingCountryId] = useState<string>();
	const [showDiscardWarning, setShowDiscardWarning] = useState(false);
	const [submitError, setSubmitError] = useState(false);
	const attachmentSessionId = useMemo(() => window.crypto.randomUUID(), []);

	const { data: countriesData, loading: countriesLoading } =
		useGetAllCountriesQuery();
	const { data: preferredData, loading: preferredLoading } =
		useGetPreferredCountryQuery();

	const countries = useMemo(
		() => (countriesData?.countries ?? []) as Country[],
		[countriesData]
	);

	const preferredCountryId =
		preferredData?.preferredCountryDcrs.__typename === 'DCRResults'
			? (preferredData.preferredCountryDcrs.results[0]?.country?.id ??
				undefined)
			: undefined;

	const countryId =
		pickedCountryId ?? (countries.length === 1 ? countries[0].id : undefined);
	const step: Step = countryId ? formStep : 'PICK_COUNTRY';
	const selectedCountryId = pendingCountryId ?? preferredCountryId;

	const dataEnabled = !!countryId;
	const { picklists, loading: picklistsLoading } = usePicklistValues({
		enabled: dataEnabled,
		countryId
	});
	const { unavailableFieldIds, loading: unavailableLoading } =
		useUnavailableFields({ enabled: dataEnabled, entityType, countryId });

	const schema = useMemo(
		() =>
			buildDcrFormSchema(entityType, {
				t,
				countryId,
				unavailable: unavailableFieldIds
			}),
		[entityType, t, countryId, unavailableFieldIds]
	);

	const methods = useForm<FieldValues>({
		resolver: zodResolver(schema) as Resolver<FieldValues>,
		defaultValues: buildDcrFormDefaults(entityType),
		mode: 'onChange',
		shouldUnregister: false,
		criteriaMode: 'all'
	});

	const values = useWatch({ control: methods.control });
	const attachments = (values.attachments ?? []) as DcrAttachment[];

	const hasEntityChanges = useMemo(() => {
		const changes = toDcrInput(
			entityType,
			values,
			unavailableFieldIds
		) as Record<string, unknown>;
		return Object.keys(changes).some(key => key !== 'primaryCountry_V');
	}, [entityType, unavailableFieldIds, values]);

	const hasAnyChanges =
		hasEntityChanges ||
		(typeof values.notes === 'string' && values.notes.trim().length > 0) ||
		attachments.length > 0;

	useEffect(() => {
		if (scrollRef.current) scrollRef.current.scrollTop = 0;
	}, [step]);

	const loading =
		countriesLoading ||
		preferredLoading ||
		(step !== 'PICK_COUNTRY' && (picklistsLoading || unavailableLoading));

	useEffect(() => {
		if (!loading) onLoaded();
	}, [loading, onLoaded]);

	const [deleteAttachment] = useDeleteDcrAttachmentMutation();
	const [postDcr, { loading: isSubmitting }] = usePostDcrMutation({
		update(cache) {
			cache.evict({ id: 'ROOT_QUERY', fieldName: 'getDcrs' });
			cache.gc();
		}
	});

	const handleAttachmentsChange = useCallback(
		(next: DcrAttachment[]) =>
			methods.setValue('attachments', next, {
				shouldDirty: true,
				shouldValidate: true
			}),
		[methods]
	);

	const { addFiles, removeAttachment } = useAttachmentUpload({
		attachments,
		onChange: handleAttachmentsChange,
		sessionId: attachmentSessionId
	});

	const discardAndClose = useCallback(() => {
		if (step !== 'SUCCESS') {
			(
				methods.getValues('attachments') as DcrAttachment[] | undefined
			)?.forEach(att => {
				if (att.remoteId) {
					deleteAttachment({ variables: { attachmentId: att.remoteId } }).catch(
						() => {}
					);
				}
			});
		}
		onClose();
	}, [deleteAttachment, methods, onClose, step]);

	const handleClose = () => {
		if (hasAnyChanges && step !== 'SUCCESS') {
			if (document.activeElement instanceof HTMLElement) {
				document.activeElement.blur();
			}
			setShowDiscardWarning(true);
			return;
		}

		discardAndClose();
	};

	const handleBackToForm = () => {
		methods.setValue('isThirdPartyDataConfirmed', false);
		setSubmitError(false);
		setFormStep('FORM');
	};

	const handleSubmit = async (submitted: FieldValues) => {
		setSubmitError(false);

		const entity = toDcrInput(
			entityType,
			{ ...submitted, primaryCountry_V: countryId },
			unavailableFieldIds
		);
		const note =
			typeof submitted.notes === 'string' && submitted.notes.trim()
				? submitted.notes.trim()
				: undefined;
		const attachmentIds = ((submitted.attachments ?? []) as DcrAttachment[])
			.filter(att => att.status === 'success' && !!att.remoteId)
			.map(att => att.remoteId as string);

		try {
			const { data } = await postDcr({
				variables: {
					payload: {
						note,
						attachmentIds: attachmentIds.length > 0 ? attachmentIds : undefined,
						entity:
							entityType === EntityType.Hcp
								? { hcp: entity as DcrhcpInput }
								: { hco: entity as DcrhcoInput }
					}
				}
			});

			if (data?.postDcr.__typename === 'DCR') {
				setFormStep('SUCCESS');
			} else {
				setSubmitError(true);
			}
		} catch {
			setSubmitError(true);
		}
	};

	const title = (
		<Box sx={sx.title}>
			<Box sx={sx.mainHeader}>
				<Typography sx={sx.textTitle} variant='h2'>
					{entityType === EntityType.Hcp ? t('Add HCP') : t('Add HCO')}
				</Typography>

				<Box sx={sx.titleActions}>
					{step === 'SUCCESS' && !pathname.includes(paths.dcrs) && (
						<Button
							sx={sx.dcrLink}
							variant='text'
							component={Link}
							to={paths.dcrs}
							onClick={onClose}
						>
							{t('View my DCRs')}
						</Button>
					)}
					<IconButton
						onClick={handleClose}
						aria-label='Close dialog'
						sx={sx.closeIcon}
					>
						<CloseIcon fontSize='small' />
					</IconButton>
				</Box>
			</Box>
		</Box>
	);

	const footer = loading
		? undefined
		: {
				PICK_COUNTRY: (
					<PickCountryStepActions
						onClose={handleClose}
						onNext={() => setPickedCountryId(selectedCountryId)}
						canContinue={!!selectedCountryId}
					/>
				),
				FORM: (
					<FormStepActions
						onClose={handleClose}
						onReview={() => setFormStep('REVIEW')}
						hasChanges={hasEntityChanges}
					/>
				),
				REVIEW: (
					<ReviewStepActions
						onBack={handleBackToForm}
						onClose={handleClose}
						attachments={attachments}
						addFiles={addFiles}
						removeAttachment={removeAttachment}
						isSubmitting={isSubmitting}
						submitError={submitError}
					/>
				),
				SUCCESS: undefined
			}[step];

	return (
		<>
			<FormProvider {...methods}>
				<DcrFormFieldsProvider
					entityType={entityType}
					countryId={countryId}
					picklists={picklists}
					unavailable={unavailableFieldIds}
				>
					<Modal
						open={open}
						onClose={handleClose}
						onExited={onExited}
						size='mdlg'
						fullHeight={false}
						disableEscapeKeyDown
						hideCloseButton
						onSubmit={methods.handleSubmit(handleSubmit)}
						bodyRef={scrollRef}
						title={title}
						showHeaderDivider={!loading && step !== 'SUCCESS'}
						footer={footer}
						footerSpacing={step === 'REVIEW'}
						customSx={sx.modal(step === 'REVIEW')}
					>
						<Box sx={sx.content}>
							{loading && <LoadingIndicator customSx={sx.loadingIndicator} />}

							<Box sx={sx.steps}>
								{!loading && step === 'PICK_COUNTRY' && (
									<PickCountryStep
										entityType={entityType}
										options={countries}
										preferredCountryId={preferredCountryId}
										value={selectedCountryId}
										onChange={setPendingCountryId}
									/>
								)}

								{countryId && (
									<Box sx={sx.formStep(step === 'FORM' && !loading)}>
										<EntityForm />
									</Box>
								)}

								{!loading && step === 'REVIEW' && <ReviewStep />}

								{!loading && step === 'SUCCESS' && (
									<DcrSuccessModalContent onClose={onClose} />
								)}
							</Box>
						</Box>
					</Modal>
				</DcrFormFieldsProvider>
			</FormProvider>

			<DiscardChangesWarningModal
				open={showDiscardWarning}
				handleHideUnsavedDialog={() => setShowDiscardWarning(false)}
				onClose={discardAndClose}
			/>
		</>
	);
};
